import React, { useMemo, useState } from 'react';
import MapaPlano, { MarcadorCamera } from './MapaPlano';
import {
  contarStatus, formatarDesde, infoStatus, ORDEM_STATUS,
} from '../../helpers/camerasStatus';

// Modo mapa da tela de cameras: a planta / foto aerea com as cameras no lugar.
// Clicar numa camera abre ela sozinha (HD); a cor mostra se esta funcionando.
// Ao lado, as cameras com problema neste mapa — o roteiro da manutencao.

export default function MapaCameras({
  mapas, mapaId, onTrocarMapa, camerasPorId, gravadoresPorId, status, podeConfigurar, onAbrirCamera,
}) {
  const [mostrarNomes, setMostrarNomes] = useState(false);
  const mapa = mapas.find((m) => m.id === mapaId) || mapas[0] || null;

  // So o que existe no catalogo (camera visivel, gravador ativo).
  const itens = useMemo(
    () => (mapa ? mapa.itens.filter((it) => camerasPorId.has(it.camera_id)) : []),
    [mapa, camerasPorId],
  );
  const camerasDoMapa = useMemo(() => itens.map((it) => camerasPorId.get(it.camera_id)), [itens, camerasPorId]);
  const contagem = contarStatus(itens.map((it) => it.camera_id), status.porCamera);

  const problemas = useMemo(() => camerasDoMapa
    .map((c) => ({ camera: c, ...(status.porCamera.get(c.id) || { status: 'desconhecido' }) }))
    .filter((p) => p.status === 'sem_video' || p.status === 'offline')
    .sort((a, b) => ORDEM_STATUS.indexOf(a.status) - ORDEM_STATUS.indexOf(b.status)
      || String(a.camera.nome).localeCompare(String(b.camera.nome), 'pt-BR')), [camerasDoMapa, status]);

  // Mapas agrupados por filial no seletor.
  const porFilial = useMemo(() => {
    const grupos = new Map();
    for (const m of mapas) {
      const chave = m.filial ? `Filial ${m.filial}` : 'Sem filial';
      if (!grupos.has(chave)) grupos.set(chave, []);
      grupos.get(chave).push(m);
    }
    return [...grupos.entries()];
  }, [mapas]);

  if (!mapa) {
    return (
      <div className="cameras-vazio">
        <i className="bi bi-map" style={{ fontSize: 40, opacity: 0.5 }} />
        <strong>Nenhum mapa cadastrado</strong>
        <span>
          {podeConfigurar
            ? 'Envie a planta ou a foto aérea em "Configurar" › Mapas e posicione as câmeras.'
            : 'Peça para a equipe de TI cadastrar os mapas das lojas.'}
        </span>
      </div>
    );
  }

  const nomeDe = (camera) => camera.nome;
  const tituloDe = (camera) => {
    const st = status.porCamera.get(camera.id);
    const info = infoStatus(st?.status);
    const g = gravadoresPorId.get(camera.gravador_id);
    const desde = st?.status && st.status !== 'ok' && st.status !== 'desconhecido' ? ` (${formatarDesde(st.desde)})` : '';
    return `${camera.nome}${g ? ` · ${g.nome} CH ${camera.canal}` : ''}\n${info.rotulo}${desde}`;
  };

  return (
    <div className="mapa-cameras">
      <div className="mapa-cameras__topo">
        <select
          className="cameras-barra__select"
          value={mapa.id}
          onChange={(e) => onTrocarMapa(Number(e.target.value))}
          aria-label="Mapa"
        >
          {porFilial.map(([rotulo, lista]) => (
            <optgroup key={rotulo} label={rotulo}>
              {lista.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </optgroup>
          ))}
        </select>

        <div className="mapa-cameras__contagem">
          {['ok', 'sem_video', 'offline', 'desconhecido'].map((s) => (contagem[s] ? (
            <span key={s} className="mapa-cameras__chip" title={infoStatus(s).rotulo}>
              <span className="mapa-cameras__bolinha" style={{ background: infoStatus(s).cor }} />
              {contagem[s]} {infoStatus(s).rotulo.toLowerCase()}
            </span>
          ) : null))}
        </div>

        <label className="mapa-cameras__opcao">
          <input type="checkbox" checked={mostrarNomes} onChange={(e) => setMostrarNomes(e.target.checked)} />
          Nomes
        </label>
        {status.atualizadoEm ? (
          <span className="cameras-barra__sub" title="O portal pergunta aos equipamentos a cada 2 minutos">
            Status {formatarDesde(status.atualizadoEm)}
          </span>
        ) : (
          <span className="cameras-barra__sub">Verificando status…</span>
        )}
      </div>

      <div className="mapa-cameras__corpo">
        <MapaPlano url={mapa.imagem_url}>
          {itens.map((it) => {
            const camera = camerasPorId.get(it.camera_id);
            return (
              <MarcadorCamera
                key={it.camera_id}
                item={it}
                nome={nomeDe(camera)}
                status={status.porCamera.get(camera.id)?.status || 'desconhecido'}
                mostrarNome={mostrarNomes}
                titulo={tituloDe(camera)}
                onClick={() => onAbrirCamera(camera, camerasDoMapa)}
              />
            );
          })}
        </MapaPlano>

        {problemas.length ? (
          <aside className="mapa-cameras__problemas">
            <strong><i className="bi bi-tools" /> Precisam de atenção</strong>
            {problemas.map((p) => (
              <button
                key={p.camera.id}
                type="button"
                className="mapa-cameras__problema"
                onClick={() => onAbrirCamera(p.camera, camerasDoMapa)}
              >
                <span className="mapa-cameras__bolinha" style={{ background: infoStatus(p.status).cor }} />
                <span>
                  <span className="cell-strong">{p.camera.nome}</span>
                  <small className="cell-muted">
                    {infoStatus(p.status).rotulo} · {formatarDesde(p.desde)}
                  </small>
                </span>
              </button>
            ))}
          </aside>
        ) : null}
      </div>

      {itens.length === 0 ? (
        <div className="mapa-cameras__aviso">
          Nenhuma câmera posicionada neste mapa ainda
          {podeConfigurar ? ' — posicione em "Configurar" › Mapas.' : '.'}
        </div>
      ) : null}
    </div>
  );
}
